import { useEffect, useRef, useState } from "react";
import { Move, ZoomIn, ZoomOut, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * BannerAdjuster
 * --------------
 * In-place editor for a user's Roots banner. Lets the owner drag the
 * image to pan and use a slider (or +/- buttons) to zoom in/out. Saves
 * three fields:
 *   banner_offset_x  (0-100, CSS object-position X %)
 *   banner_offset_y  (0-100, CSS object-position Y %)
 *   banner_scale     (1.0-3.0, transform: scale multiplier)
 *
 * Rendering model (kept identical to Roots.jsx so what you see here is
 * *exactly* what you get after Save):
 *   img {
 *     object-fit: cover;
 *     object-position: {x}% {y}%;
 *     transform: scale({s});
 *     transform-origin: {x}% {y}%;
 *   }
 *
 * Interactions:
 *   • Pointer drag inside the banner pans the image. On touch we translate
 *     the delta into object-position % so we don't fight the browser's
 *     built-in touch panning (pointer events give us both mouse and touch
 *     with the same handler).
 *   • +/- buttons or the slider control zoom. Scale is clamped 1-3x, which
 *     matches the server clamp so we can't drift out of sync.
 *   • Reset button snaps back to defaults (50/50/1.0).
 *   • Save calls `onSave({offset_x, offset_y, scale})` — the parent
 *     handles the actual PATCH so this component stays presentational.
 */

const CLAMP_SCALE = (s) => Math.max(1, Math.min(3, s));
const CLAMP_PCT = (p) => Math.max(0, Math.min(100, p));

export default function BannerAdjuster({
  src,
  initial = { banner_offset_x: 50, banner_offset_y: 50, banner_scale: 1 },
  saving = false,
  onSave,
  onCancel,
}) {
  const [x, setX] = useState(initial.banner_offset_x ?? 50);
  const [y, setY] = useState(initial.banner_offset_y ?? 50);
  const [scale, setScale] = useState(initial.banner_scale ?? 1);
  const wrapRef = useRef(null);
  // Drag state — kept in a ref so we don't re-render every mousemove.
  const drag = useRef({ active: false, startX: 0, startY: 0, startPctX: 0, startPctY: 0, w: 0, h: 0 });

  const onPointerDown = (e) => {
    if (!wrapRef.current) return;
    const box = wrapRef.current.getBoundingClientRect();
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPctX: x,
      startPctY: y,
      w: box.width,
      h: box.height,
    };
    wrapRef.current.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    // Convert pixel drag → object-position %. Divide by the container's
    // dimension so a drag across the whole banner shifts by ~100%.
    // Inverted because dragging RIGHT should reveal MORE of the LEFT side
    // of the image (i.e. object-position X gets smaller).
    // Sensitivity also scaled inversely by zoom — at 2x the image is twice
    // as big, so the same drag should move object-position half as much
    // for a natural 1-to-1 feel.
    const sensX = drag.current.w * Math.max(1, scale);
    const sensY = drag.current.h * Math.max(1, scale);
    const nextX = CLAMP_PCT(drag.current.startPctX - (dx / sensX) * 100);
    const nextY = CLAMP_PCT(drag.current.startPctY - (dy / sensY) * 100);
    setX(nextX);
    setY(nextY);
  };
  const onPointerUp = (e) => {
    drag.current.active = false;
    wrapRef.current?.releasePointerCapture?.(e.pointerId);
  };

  // Prevent page scroll while dragging on touch. Passive-safe: we only
  // attach when a drag is actually in progress so scrolling elsewhere on
  // the page is unaffected.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const stopIfDragging = (e) => { if (drag.current.active) e.preventDefault(); };
    el.addEventListener("touchmove", stopIfDragging, { passive: false });
    return () => el.removeEventListener("touchmove", stopIfDragging);
  }, []);

  const nudgeZoom = (delta) => setScale((s) => CLAMP_SCALE(+(s + delta).toFixed(2)));
  const reset = () => { setX(50); setY(50); setScale(1); };

  const style = {
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${scale})`,
    transformOrigin: `${x}% ${y}%`,
    transition: drag.current.active ? "none" : "transform 120ms ease, object-position 120ms ease",
  };

  return (
    <div data-testid="banner-adjuster" className="rounded-2xl border border-[#B79256]/40 bg-[#FBF8F1] p-3 sm:p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm text-[#2A2823]/85">
        <Move className="w-4 h-4 text-[#B79256]" strokeWidth={1.75} />
        <span>Drag the image to reposition. Use the slider to zoom.</span>
      </div>

      <div
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="banner-adjuster-preview"
        className="relative w-full aspect-[3/1] sm:aspect-[3.2/1] min-h-[140px] rounded-2xl overflow-hidden bg-[#F0E6D0] border border-[#B79256]/20 touch-none select-none cursor-grab active:cursor-grabbing"
      >
        <img
          src={src}
          alt=""
          draggable={false}
          data-testid="banner-adjuster-img"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={style}
        />
        {/* Faint grid so the user has a visual anchor while dragging */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(42,40,35,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(42,40,35,0.08) 1px, transparent 1px)",
            backgroundSize: "25% 33%",
          }}
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="banner-zoom-out"
          onClick={() => nudgeZoom(-0.1)}
          disabled={scale <= 1}
          className="w-9 h-9 rounded-full border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/10 disabled:opacity-40 flex items-center justify-center"
          aria-label="Zoom out"
        >
          <ZoomOut className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <input
          type="range"
          data-testid="banner-zoom-slider"
          min="1"
          max="3"
          step="0.05"
          value={scale}
          onChange={(e) => setScale(CLAMP_SCALE(parseFloat(e.target.value)))}
          className="flex-1 accent-[#4E5A3D]"
          aria-label="Zoom level"
        />
        <button
          type="button"
          data-testid="banner-zoom-in"
          onClick={() => nudgeZoom(0.1)}
          disabled={scale >= 3}
          className="w-9 h-9 rounded-full border border-[#B79256]/40 bg-white text-[#2A2823] hover:bg-[#B79256]/10 disabled:opacity-40 flex items-center justify-center"
          aria-label="Zoom in"
        >
          <ZoomIn className="w-4 h-4" strokeWidth={1.75} />
        </button>
        <span data-testid="banner-zoom-value" className="text-xs text-[#2A2823]/55 w-10 text-right">
          {scale.toFixed(2)}x
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          data-testid="banner-adjuster-reset"
          onClick={reset}
          disabled={saving}
          className="text-xs text-[#2A2823]/55 hover:text-[#2A2823] underline underline-offset-2"
        >
          reset
        </button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            data-testid="banner-adjuster-cancel"
            variant="ghost"
            disabled={saving}
            onClick={onCancel}
            className="rounded-full h-9 px-4 text-sm text-[#2A2823] hover:bg-[#B79256]/10"
          >
            <X className="w-4 h-4 mr-1" strokeWidth={1.75} /> Cancel
          </Button>
          <Button
            type="button"
            data-testid="banner-adjuster-save"
            disabled={saving}
            onClick={() =>
              onSave?.({
                banner_offset_x: Math.round(x * 10) / 10,
                banner_offset_y: Math.round(y * 10) / 10,
                banner_scale: Math.round(scale * 100) / 100,
              })
            }
            className="rounded-full h-9 px-4 text-sm bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] shadow-none"
          >
            <Check className="w-4 h-4 mr-1" strokeWidth={2} /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
